import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, CardBody, Table, Button, Input, Spinner, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { productActions } from '../../sagas/productSlice';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaPrint, FaSearch } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import JsBarcode from 'jsbarcode';
import jsPDF from 'jspdf';
import { debounce } from 'lodash';

const BarcodePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedProducts, setSelectedProducts] = useState({}); // { productId: quantity }
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [criteria, setSearchCriteria] = useState({ query: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);

  const {
    products,
    loading: productsLoading,
    totalPages,
    totalElements,
    pageNumber,
    pageSize,
    error,
  } = useSelector((state) => state.products);
  const searchInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Auto focus on mount
    searchInputRef.current?.focus();

    // Add keyboard shortcut
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update product fetch effect
  useEffect(() => {
    dispatch(productActions.findAll({
      search: "",
      page: 0,
      size: 10,
    }));
  }, [dispatch]); // Remove selectedProducts from here

  useEffect(() => {
    const savedSelections = localStorage.getItem('selectedBarcodeProducts');
    if (savedSelections) {
      setSelectedProducts(JSON.parse(savedSelections));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('selectedBarcodeProducts', JSON.stringify(selectedProducts));
  }, [selectedProducts]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setIsSearching(true);
      dispatch(productActions.findAll({
        search: query,
        page: 0,
        size: 10,
      }));
    }, 500), // Wait 500ms after last keystroke
    []
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };


  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedProducts(products.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (product, quantity = 1) => {
    setSelectedProducts(prev => {
      if (prev[product.id]) {
        const { [product.id]: removed, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [product.id]: {
          product,
          quantity
        }
      };
    });
  };

  // Update quantity handler
  const handleQuantityChange = (productId, quantity) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        quantity: parseInt(quantity) || 1
      }
    }));
  };

  const generateBarcodes = async () => {
    if (Object.keys(selectedProducts).length === 0) return;

    setLoading(true);
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        margins: {
          top: 5,
          left: 8,
          right: 8,
          bottom: 5,
        },
      });

      // Fixed margins and layout settings
      const settings = {
        marginTop: 5,
        marginLeft: 8,
        barcodeWidth: 45,
        barcodeHeight: 17,
        textHeight: 10,
        colsPerPage: 4,
        spacingX: 2,
        spacingY: 5,
      };

      const totalHeight = settings.barcodeHeight + settings.textHeight;
      let currentX = settings.marginLeft;
      let currentY = settings.marginTop;
      let itemsInPage = 0;

      Object.entries(selectedProducts).forEach(([productId, { product, quantity }]) => {
        for (let i = 0; i < quantity; i++) {
          if (itemsInPage > 0 && itemsInPage % (settings.colsPerPage * 15) === 0) {
            doc.addPage();
            currentX = settings.marginLeft;
            currentY = settings.marginTop;
            itemsInPage = 0;
          }

          // Create barcode
          const canvas = document.createElement('canvas');
          canvas.style.quality = 'best';

          JsBarcode(canvas, product.code || product.id.toString(), {
            format: "CODE128",
            width: 2,
            height: 40,
            displayValue: false, // Hide default barcode text
            margin: 4,
            background: '#FFFFFF',
            lineColor: '#000000',
          });

          // Add barcode to PDF
          const imageData = canvas.toDataURL('image/jpeg', 1.0);
          doc.addImage(imageData, 'JPEG', currentX, currentY, settings.barcodeWidth, settings.barcodeHeight);

          // Add product details below the barcode (centered and without keys)
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          const centerX = currentX + settings.barcodeWidth / 2;
          doc.text(product.code, centerX, currentY + settings.barcodeHeight + 4, { align: "center" });
          doc.text(product.libelle, centerX, currentY + settings.barcodeHeight + 8, { align: "center" });
          doc.text(`${product.prixVenteTTC1.toFixed(2)} DH`, centerX, currentY + settings.barcodeHeight + 12, { align: "center" });

          // Update position
          itemsInPage++;
          if (itemsInPage % settings.colsPerPage === 0) {
            currentX = settings.marginLeft;
            currentY += totalHeight + settings.spacingY;
          } else {
            currentX += settings.barcodeWidth + settings.spacingX;
          }
        }
      });

      doc.save('barcodes.pdf');
    } catch (error) {
      console.error('Error generating barcodes:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <Card className="shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">
                  <FaBarcode className="me-2" />
                  {t('barcode.title')}
                </h4>
                <Button
                  color="primary"
                  onClick={generateBarcodes}
                  disabled={selectedProducts.length === 0 || loading}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FaPrint className="me-1" />
                      {t('barcode.generate')}
                    </>
                  )}
                </Button>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                  <div className="mt-2 text-muted">
                    {t('common.loadingData')}
                  </div>
                </div>
              ) : (<>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  {/* Search Input - Left Side */}
                  <div className="position-relative flex-grow-1 me-3">
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      placeholder={t('product.searchFocusHint')}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className={`ps-4 pe-5 ${isSearching ? 'searching' : ''}`}
                      title={t('product.searchShortcut')}
                    />
                    <FaSearch
                      style={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#adb5bd'
                      }}
                      size={14}
                    />
                    <FaBarcode
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#adb5bd'
                      }}
                      size={20}
                      title={t('product.scanHint')}
                    />
                  </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <Table responsive bordered hover>
                    <thead>
                      <tr>
                        <th width="50">
                          <Input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>{t('product.code')}</th>
                        <th>{t('product.description')}</th>
                        <th>{t('product.barcode')}</th>
                        <th width="100">{t('product.quantity')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productsLoading ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            <Spinner color="primary" />
                          </td>
                        </tr>
                      ) : !products || products.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            {t('product.noProducts')}
                          </td>
                        </tr>
                      ) : (
                        products.map(product => (
                          <tr key={product.id}>
                            <td>
                              <Input
                                type="checkbox"
                                checked={selectedProducts[product.id] !== undefined}
                                onChange={() => handleSelectProduct(product)}
                              />
                            </td>
                            <td>{product.code}</td>
                            <td>{product.libelle}</td>
                            <td>{product.codeBarre || 'Non spécifié'}</td>
                            <td width="100">
                              {selectedProducts[product.id] !== undefined && (
                                <Input
                                  type="number"
                                  onFocus={(e) => e.target.select()} // Select the field content on focus
                                  min="1"
                                  value={selectedProducts[product.id].quantity}
                                  onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                                  className="form-control-sm"
                                />
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </>)}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BarcodePage;